/* eslint-disable no-nested-ternary */
import React, {useCallback, useEffect} from 'react';

// material-ui
import {
    Box,
    Grid,
    Typography,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    Chip,
    Dialog,
    Input,
    IconButton,
    Tooltip
} from '@mui/material';
import {useTheme} from '@mui/styles';

import {useDispatch, useSelector} from 'react-redux';
import {getSpecificCustomer, selectCustomerById} from 'store/customers/customersSlice';
import {RootState} from 'store';

// project imports
import config from 'config';
import SubCard from 'ui-component/cards/SubCard';
import Logo from 'ui-component/Logo';
import MyChip from 'ui-component/extended/Chip';

import {IconCurrencyShekel, IconEye, IconEyeOff, IconSum} from '@tabler/icons';

// Locale
import {format} from 'date-fns';
import {enUS, he} from 'date-fns/locale';
import {FormattedMessage} from 'react-intl';

// types
import {TaxNote} from 'types/DocumentsTypes/TaxNote';
import CustomerProductsPreviewList from '../../products/CustomerProductsPreviewList';
import IItem from 'types/product_delivery';
import DocumentHeaderPreview from '../document_header_preview/DocumentHeaderPreview';
import {roundDecimals} from '../../../../../fromKotlin/nkutils';
import DocumentClientDetails from '../document_header_preview/DocumentClientDetails';
import {NoNegativeInput} from '../../../../../ui-component/textFieldNumber';
import PreviewDocumentComments from '../PreviewComments';
import {deserializeDocument} from "../../../../../types/DocumentsTypes/utils";
import {getFile} from "../../../../../api/customer/files.api";
import {
    hashIsraelToken,
    hashSign,
    hashIsraelErrorText,
    productsFromNotes,
    buildNotesSum,
    docResultsDeserialize
} from "../../../../../api/customer/documents.api";
import ExternalSoftwareSendChip from '../ExternalSoftwareSendChip';
import DocumentApiSendChip from '../DocumentApiSendChip';
import JSDB from "../../../../../fromKotlin/nk";
import {SNACKBAR_OPEN} from "../../../../../store/actions";
import Swal from "sweetalert2";
import {displayDocumentId, ExtendedDocument, isSupplierDocument} from "../../../../../types/DocumentsTypes/Document";
import {getSpecificSupplier, getSuppliers, selectSupplierById} from "../../../../../store/suppliers/suppliersSlice";
import GenerativeLoader from "../../../../../ui-component/GenerativeLoader";
import FunctionsIcon from "@mui/icons-material/Functions";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {refreshDocs} from "../../../../../hooks/customer/documents.hooks";
import DocumentNew from "../DocumentNew";
import {
    applySupplierMoneyEdit,
    isSupplierTaxDocument,
    SupplierDocumentDate,
    SupplierEntityPicker,
    SupplierHandLines,
    SupplierMoneyInput,
    SupplierTotalsDiscountLabel,
    useSupplierTaxLineSync
} from './supplierTaxEdit';

interface InvoicePreviewProps extends TaxNote {
    onProductChange?: (product: IItem) => void;
    onIdChange?: (num) => void;
    editting?: boolean;
    setInternalEdit?: (edit: any) => void;
    internalEdit?: any;
    pool?: string
    outerDoc?: boolean
    onCreateReceipt?: () => void;
    onInvoiceRefreshed?: (doc: ExtendedDocument) => void;
}

function taxPreviewTitle(type?: string): string {
    switch (type) {
        case 'invoice':
            return 'חשבונית מס';
        case 'refund':
            return 'חשבונית מס זיכוי';
        case 'tax_invoice':
            return 'חשבונית מס קבלה';
        case 'sup_invoice':
            return 'חשבונית ספק';
        case 'sup_refund':
            return 'זיכוי ספק';
        default:
            return 'חשבונית/קבלה ספק';
    }
}

const canIssueReceiptFromInvoice = (document: { id?: number; status?: string; type?: string }) =>
    !!document?.id &&
    document.id !== 0 &&
    (document.status === 'open' || document.status === 'partly_closed') &&
    (document.type === 'invoice' || document.type === 'refund' || document.type === 'sup_invoice' || document.type === 'sup_refund');

const InvoicePreview = ({
                            onProductChange,
                            onIdChange,
                            editting,
                            internalEdit,
                            pool,
                            setInternalEdit,
                            outerDoc,
                            onCreateReceipt,
                            onInvoiceRefreshed,
                            ...document
                        }: InvoicePreviewProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    // TODO: Change to single customer fetching

    const supplierEditable = !!editting && isSupplierTaxDocument(document.type);
    const entityId = supplierEditable ? (internalEdit?.entId ?? document.entId) : document.entId;
    const customer = isSupplierDocument(document.type) ? useSelector((state: RootState) => selectSupplierById(state, entityId)) : useSelector((state: RootState) => selectCustomerById(state, entityId));

    const [pfromNotes, setPfromNotes] = React.useState<any>([]);
    React.useEffect(() => {
        if (!customer) {
            if (isSupplierDocument(document.type)) {
                dispatch(getSpecificSupplier(Number(entityId)));
                dispatch(getSuppliers());
            } else {
                dispatch(getSpecificCustomer(Number(entityId)));
            }
        }
    }, [dispatch, customer, entityId, document.type]);

    const address = document.details?.street && document.details?.streetNumber ? `${document.details.street}, ${document.details.streetNumber}` : document.details?.street;
    const apiConfig = JSDB().apiConfig
    const [displayOrderProducts, setDisplayOrderProducts] = React.useState(false);
    const [receiptOpen, setReceiptOpen] = React.useState(false);
    const [hideCostAndGain, setHideCostAndGain] = React.useState(false);
    const refresher = refreshDocs(pool)
    const shownDate = internalEdit?.date ?? document.date;
    const shown = supplierEditable ? {...document, ...(internalEdit || {})} : document;
    useSupplierTaxLineSync({
        enabled: supplierEditable,
        products: document.products,
        handArray: internalEdit?.handArray ?? document.handArray,
        date: shownDate,
        entId: document.entId,
        discount: internalEdit?.discount ?? document.discount,
        deliveryCertificatesIds: document.deliveryCertificatesIds,
        hand: document.hand,
        setInternalEdit
    });
    const canCreateReceipt = !editting && canIssueReceiptFromInvoice(document);
    const receiptType = isSupplierDocument(document.type) ? 'sup_receipt' : 'receipt';
    const openReceiptDialog = React.useCallback(() => {
        if (onCreateReceipt) {
            onCreateReceipt();
            return;
        }
        setReceiptOpen(true);
    }, [onCreateReceipt]);
    const refreshInvoiceAfterReceipt = React.useCallback(async () => {
        if (document?.id && document?.nkObject?.getConnectedDocType) {
            try {
                const refreshedDocs = await refresher.mutateAsync({
                    ids: [Number(document.id)],
                    types: [document.nkObject.getConnectedDocType()],
                    offline: false,
                    full: true
                });
                const refreshed = (refreshedDocs as ExtendedDocument[] | undefined)?.find(
                    (d) => Number(d.id) === Number(document.id) && d.type === document.type
                );
                if (refreshed) onInvoiceRefreshed?.(refreshed);
            } catch (e) {
                console.log(e);
            }
        }
    }, [document, refresher, onInvoiceRefreshed]);
    const closeReceiptDialog = React.useCallback(async () => {
        await refreshInvoiceAfterReceipt();
        setReceiptOpen(false);
    }, [refreshInvoiceAfterReceipt]);
    const handleBuildNotesSum = useCallback(async (setLoader) => {
        try {
            const ids = (document.deliveryCertificatesIds || []).map((x: any) => Number(x)).filter((x: number) => !isNaN(x));
            const taxIds = [Number(document.id)].filter((x) => !isNaN(x));
            if (ids.length === 0 || taxIds.length === 0) {
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'אין תעודות או חשבונית תקפה לחישוב',
                    variant: 'alert',
                    alertSeverity: 'warning'
                });
                return;
            }
            getFile(false, () => buildNotesSum(undefined, ids, undefined, false), `סיכום חשבון `, undefined, undefined, setLoader);
            await
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'סיכום התעודות נבנה בהצלחה',
                    variant: 'alert',
                    alertSeverity: 'success'
                });
        } catch (e: any) {
            Swal.fire({
                title: 'שגיאה בבניית סיכום תעודות',
                target: '#custom-target',
                text: e?.message || 'אירעה שגיאה בעת ביצוע הפעולה',
                icon: 'error',
                showCancelButton: false,
                confirmButtonColor: '#d63030'
            });
        }
    }, [document, dispatch]);
    const startLog = useCallback(async () => {
        if (!document || (document.type != 'invoice' && document.type != 'refund')) return
        // if(true) return
        try {
            const x = await hashSign(document.id, document.type === 'invoice' ? 0 : document.type === 'refund' ? 2 : 1)
            console.log(x)
            if (!x) {
                // token err
                // const newWindow = window.open('https://openapi.taxes.gov.il/shaam/tsandbox/longtimetoken/oauth2/authorize?response_type=code&client_id=e65677412ee9bcfc582f4b7f6f2d801f&scope=scope&redirect_uri=http://localhost:3001/reports/tax-israel', '_blank', 'noopener,noreferrer');
                const newWindow = window.open('https://openapi.taxes.gov.il/shaam/production/longtimetoken/oauth2/authorize?response_type=code&client_id=35828e73b8b9edbc05c4ac943f5634f0&scope=invoices_scope&redirect_uri=https://solvitcompany.com/reports/tax-israel', '_blank', 'noopener,noreferrer');
                if (newWindow) newWindow.opener = null;
                return

            }
            if (x.doc) {
                if (setInternalEdit) {
                    setInternalEdit({...internalEdit, taxConfirmation: (x.doc.taxConfirmation)})
                }
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'טוקן החשבונית עודכן בהצלחה!',
                    variant: 'alert',
                    alertSeverity: 'success'
                });
            } else {
                Swal.fire({
                    title: 'לא הצליח לממשק מס הקצאה לחשבונית',
                    target: '#custom-target',
                    text: hashIsraelErrorText(x.text),
                    icon: 'error',
                    showCancelButton: false,
                    confirmButtonColor: '#d63030'
                });
            }


        } catch (e) {
            console.log(e)
            Swal.fire({
                title: 'לא הצליח לממשק מס הקצאה לחשבונית',
                target: '#custom-target',
                text: hashIsraelErrorText(e),
                icon: 'error',
                showCancelButton: false,
                confirmButtonColor: '#d63030'
            });
        }

    }, [document])
    const notes2 = JSDB().getUser().first?.getJsonDesc("notes2")?.name ?? 'הערות 2'
    const notes3 = JSDB().getUser().first?.getJsonDesc("notes3")?.name ?? 'הערות 3'
    const notes4 = JSDB().getUser().first?.getJsonDesc("notes4")?.name ?? 'הערות 4'
    const notes5 = JSDB().getUser().first?.getJsonDesc("notes5")?.name ?? 'הערות 5'
    React.useEffect(() => {
        const f = async () => {

            const products = await productsFromNotes(document) ?? []
            setPfromNotes(products)
        }
        if (document.status != 'canceled')
            f()
    }, [])

    if (!customer) return <Box/>;
    const byProducts = (document.products?.length ?? 0) > 0 || !!onProductChange;
    const gainProducts = document.products ?? [];
    const gain = byProducts
        ? roundDecimals(gainProducts.reduce((sum, x) => sum + (x.costPrice ? x.costPrice : 0) * (x.quantity + (x.quantitySecond ?? 0) * (x.conversion_ratio ?? 0)), 0))
        : 0;
    const totalBeforeTax = roundDecimals(document.mainValue - ((document.taxValue) ? document.taxValue : 0));
    const profit = roundDecimals(totalBeforeTax - gain);
    const profitPercent = totalBeforeTax > 0 ? roundDecimals((profit / totalBeforeTax) * 100) : 0;
    const showCostAndGain = byProducts && !hideCostAndGain;
    // const oldProducts = [...document.products.map((x, i) => {
    //     // return every second product in the array and change the quantity to be the between 1 to 10
    //     if (i % 2 == 0) {
    //         return {
    //             ...x,
    //             quantity: Math.floor(Math.random() * 10) + 1,
    //             quantitySecond: Math.floor(Math.random() * 10) + 1,
    //         };
    //     }
    // }).filter((x) => x != undefined), {
    //     "discount": 0,
    //     "nkObject": {
    //         "productId": 5,
    //         "value": 2,
    //         "returns": 0,
    //         "price": 6,
    //         "supplier_id": -1,
    //         "use_price": 0,
    //         "use_price_tax_note": 0,
    //         "discount": 0,
    //         "wrapped_amount": 0,
    //         "wrapped_amount_return": 0,
    //         "conversion_ratio": 0,
    //         "wrapped_unit": 0,
    //         "notes": null,
    //         "doneState": 0,
    //         "cost": null,
    //         "tempPosition": 0,
    //         "isClient": true,
    //         "useWrapped": 0,
    //         "changedPrice": 1
    //     },
    //     "quantityReturns": 0,
    //     "quantitySecondReturns": 0,
    //     "totalPrice": 6,
    //     "id": 5,
    //     "external_id": "73",
    //     "name": "חלונות",
    //     "image": "",
    //     "barcode": "666",
    //     "costPrice": 4,
    //     "position": 2,
    //     "quantity": 2,
    //     "quantitySecond": 0,
    //     "no_tax_product": false,
    //     "unit": "קרטון",
    //     "in_unit": 10,
    //     "category": "קטגוריה 1",
    //     "category2": "",
    //     "available_units": [
    //         "קרטון"
    //     ],
    //     "notes": null,
    //     "conversion_ratio": 0
    // }
    // ];
    // document.order_id = "123456789"
    // document.order_products = oldProducts;
    // END OF TEST TO REMOVE

    return (
        <SubCard
            darkTitle
            title={
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                    <Grid container sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                        <Typography fontWeight='bold'>
                            {taxPreviewTitle(document.type)} {onIdChange ?
                            <NoNegativeInput

                                placeholder={'אוטומטי'}
                                changer={(v) => {
                                    onIdChange(v)
                                }}

                            /> : displayDocumentId(document)}
                        </Typography>
                        {
                            document?.type === "invoice" && (document?.orderId && document?.nkObject?.getConnectedOrder()) ? ( //document?.old_prodcuts?.length > 0 ? (
                                <Chip
                                    label={`הזמנה ${document?.orderId}`}
                                    color={"info"}
                                    sx={{
                                        ml: 2,
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => {

                                    }}
                                />
                            ) : null
                        }
                        {canCreateReceipt ? (
                            <Chip
                                icon={<ReceiptLongIcon/>}
                                label="הנפק קבלה"
                                color="success"
                                sx={{
                                    ml: 2,
                                    cursor: 'pointer'
                                }}
                                onClick={openReceiptDialog}
                            />
                        ) : null}
                    </Grid>
                    {(document && document.id != 0 && apiConfig && apiConfig.hasOne()) ?
                        <DocumentApiSendChip
                            document={document}
                            refresher={refresher}
                            labelSent='נשלח לOne'
                            labelNotSent='לא נשלח לOne'
                            isSent={(d) => !!(d?.one?.send)}
                        /> : undefined
                    }
                    {(document && document.id != 0 && apiConfig && apiConfig.hasManager()) ?
                        <DocumentApiSendChip
                            document={document}
                            refresher={refresher}
                            labelSent='נשלח למנגר'
                            labelNotSent='לא נשלח למנגר'
                            isSent={(d) => !!(d?.manager?.send)}
                        /> : undefined
                    }
                    {(document && document.id != 0 && apiConfig && apiConfig.hasStoreNext()) ?
                        <DocumentApiSendChip
                            document={document}
                            refresher={refresher}
                            labelSent='נשלח לסטורנקס'
                            labelNotSent='לא נשלח סטורנקטס'
                            isSent={(d) => !!(d?.storeNext?.send)}
                        /> : undefined
                    }
                    {(document && document.id != 0 && apiConfig && apiConfig.hasHashavshevet()) ?
                        <DocumentApiSendChip
                            document={document}
                            refresher={refresher}
                            labelSent='נשלח לחשבשבת'
                            labelNotSent='לא נשלח לחשבשבת'
                            isSent={(d) => !!(d?.hashavshevet?.send)}
                        /> : undefined
                    }
                    <ExternalSoftwareSendChip
                        document={document}
                        refresher={refresher}
                    />
                </div>

            }
            secondary={<Logo width={50}/>}
        >
            <Grid container spacing={2}>
                <DocumentHeaderPreview/>
                <Grid item xs={12}>
                    <Grid container>
                        <DocumentClientDetails address={address} name={customer.name}
                                               businessName={document.details!.companyName}
                                               businessId={document.details!.companyId}
                                               id={customer.id} external_id={customer.externalId}
                                               phone={document.details!.phone} email={document.details!.email}/>
                        <Grid item sm={6}>
                            <Grid container>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    {`פרטי ${taxPreviewTitle(document.type)}`}
                                </Typography>
                                <Grid item xs={12}>
                                    <Grid container>
                                        <Grid item xs={4}>
                                            <Typography variant='body2'>
                                                <FormattedMessage id='Date'/>:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <SupplierDocumentDate
                                                editing={supplierEditable}
                                                date={shownDate}
                                                onChange={(date) => setInternalEdit && setInternalEdit({
                                                    ...internalEdit,
                                                    date,
                                                    date_edit: 1
                                                })}
                                            />
                                            {supplierEditable ? (
                                                <Box sx={{mt: 1}}>
                                                    <SupplierEntityPicker
                                                        entId={entityId}
                                                        entName={internalEdit?.entName ?? document.entName}
                                                        onChange={(id, name) => setInternalEdit && setInternalEdit({
                                                            ...internalEdit,
                                                            entId: id,
                                                            entName: name,
                                                            entId_edit: 1
                                                        })}
                                                    />
                                                </Box>
                                            ) : null}
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant='body2'>
                                                תאריך הנפקה:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <Typography variant='body2'>
                                                {format(document.issueDate, 'PPPPp', {
                                                    locale: config.i18n === 'he' ? he : enUS
                                                })}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                            <Typography variant='body2'>
                                                <FormattedMessage id='Status'/>:
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={8}>
                                            <MyChip
                                                label={<FormattedMessage id={document.status}/>}
                                                variant='outlined'
                                                size='small'
                                                chipcolor={
                                                    document.status === 'pending_approval'
                                                        ? 'warning'
                                                        : document.status === 'approved' || document.status === 'closed'
                                                            ? 'success'
                                                            : document.status === 'open' || document.status === 'partly_closed'
                                                                ? 'error'
                                                                : ''
                                                }
                                            />
                                        </Grid>
                                        <>
                                            <Grid item xs={4}>
                                                <Typography variant='body2'>
                                                    סוכן:
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={8}>
                                                <Typography variant='body2'>
                                                    {document.agent}
                                                </Typography>
                                            </Grid>
                                        </>
                                        {(!editting) ?
                                            <>
                                                <Grid item xs={4}>
                                                    <Typography variant='body2'>
                                                        מספר הקצאה:
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={8}>
                                                    <Typography variant='body2'>
                                                        {document.taxConfirmation}
                                                    </Typography>
                                                </Grid>
                                                {(document.type === 'sup_invoice' || document.type === 'sup_tax_invoice' || document.type === 'sup_refund') ? (
                                                    <>
                                                        <Grid item xs={4}>
                                                            <Typography variant='body2'>
                                                                מספר חשבונית ספק:
                                                            </Typography>
                                                        </Grid>
                                                        <Grid item xs={8}>
                                                            <Typography variant='body2'>
                                                                {document.supplierInvoiceNumber
                                                                    || (typeof document.nkObject?.supplierInvoiceNumber === 'function'
                                                                        ? document.nkObject.supplierInvoiceNumber()
                                                                        : '')}
                                                            </Typography>
                                                        </Grid>
                                                        {(document.supplierInputEquipment
                                                            || document.details?.supplierInputEquipment
                                                            || (typeof document.nkObject?.supplierInputEquipment === 'function'
                                                                && document.nkObject.supplierInputEquipment())) ? (
                                                            <>
                                                                <Grid item xs={4}>
                                                                    <Typography variant='body2'>
                                                                        תשומות ציוד:
                                                                    </Typography>
                                                                </Grid>
                                                                <Grid item xs={8}>
                                                                    <Typography variant='body2'>
                                                                        כן
                                                                    </Typography>
                                                                </Grid>
                                                            </>
                                                        ) : null}
                                                    </>
                                                ) : null}
                                            </> : undefined
                                        }
                                        {
                                            editting && (
                                                <>
                                                    <Grid item xs={4}>
                                                        <Typography variant='body2'>
                                                            מספר הקצאה:
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Input
                                                            type='text'
                                                            value={internalEdit?.taxConfirmation ?? document.taxConfirmation}
                                                            onChange={(e) => setInternalEdit && setInternalEdit({
                                                                ...internalEdit,
                                                                taxConfirmation: (e.target.value)
                                                            })}
                                                        />
                                                    </Grid>
                                                    {JSDB().getUser().first.hesh_israel == 1 ? <button onClick={() => {
                                                        startLog()
                                                    }}>
                                                        ממשק מס הכנסה
                                                    </button> : undefined}
                                                    {(document.type === 'sup_invoice' || document.type === 'sup_tax_invoice' || document.type === 'sup_refund') ? (
                                                        <>
                                                            <Grid item xs={4}>
                                                                <Typography variant='body2'>
                                                                    מספר חשבונית ספק:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <Input
                                                                    type='text'
                                                                    value={internalEdit?.supplierInvoiceNumber
                                                                        ?? document.supplierInvoiceNumber
                                                                        ?? ''}
                                                                    onChange={(e) => setInternalEdit && setInternalEdit({
                                                                        ...internalEdit,
                                                                        supplierInvoiceNumber: e.target.value
                                                                    })}
                                                                />
                                                            </Grid>
                                                            <Grid item xs={4}>
                                                                <Typography variant='body2'>
                                                                    תשומות ציוד:
                                                                </Typography>
                                                            </Grid>
                                                            <Grid item xs={8}>
                                                                <input
                                                                    type='checkbox'
                                                                    checked={!!(internalEdit?.supplierInputEquipment
                                                                        ?? document.supplierInputEquipment
                                                                        ?? document.details?.supplierInputEquipment)}
                                                                    onChange={(e) => setInternalEdit && setInternalEdit({
                                                                        ...internalEdit,
                                                                        supplierInputEquipment: e.target.checked,
                                                                        details_edit: 1
                                                                    })}
                                                                />
                                                            </Grid>
                                                        </>
                                                    ) : null}

                                                </>
                                            )
                                        }
                                        {document.deliveryCertificatesIds.length > 0 ? (
                                            <Grid item xs={12}>
                                                <Grid container alignItems="center" justifyContent="space-between">
                                                    <Grid item>
                                                        <Typography variant='body2'>
                                                            <FormattedMessage id="Delivery Certificates ID's"/>:
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>

                                                        סיכום
                                                        <Tooltip title="בנה סיכום מתעודות" placement="top">

                                                            <GenerativeLoader func={(setLoader) => {
                                                                handleBuildNotesSum(setLoader)
                                                            }} icon={<FunctionsIcon style={{
                                                                alignContent: 'center',
                                                                cursor: 'pointer',
                                                                width: '24px',
                                                                height: '24px',
                                                                color: 'gray'
                                                            }} fontSize='small'/>}/>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        ) : undefined}
                                        {document.deliveryCertificatesIds.map((d, i) => (
                                            <Grid key={i} item xs={4}>
                                                <Typography variant='subtitle1'>{d}</Typography>
                                            </Grid>
                                        ))}
                                        <>
                                            {(editting || outerDoc) &&
                                                <Grid container xs={12} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    <Grid item xs={4}>
                                                        <Typography variant='body2'>
                                                            {notes2}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Input
                                                            value={internalEdit?.notes2 ?? document?.notes2}
                                                            onChange={(e) => outerDoc ? outerDoc(prev => {
                                                                return {...prev, notes2: e.target.value}
                                                            }) : setInternalEdit && setInternalEdit({
                                                                ...internalEdit,
                                                                notes2: e.target.value
                                                            })}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            }
                                            {document?.notes2?.length > 0 && !(editting || outerDoc) &&
                                                <Grid container xs={12} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    <Grid item xs={4}>
                                                        <Typography variant='body2'>
                                                            {notes2}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Typography variant='body2'>
                                                            {
                                                                document?.notes2
                                                            }
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            }
                                        </>
                                        <>
                                            {(editting || outerDoc) &&
                                                <Grid container xs={12} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    <Grid item xs={4}>
                                                        <Typography variant='body2'>
                                                            {notes3}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Input
                                                            value={internalEdit?.notes3 ?? document?.notes3}
                                                            onChange={(e) => outerDoc ? outerDoc(prev => {
                                                                return {...prev, notes3: e.target.value}
                                                            }) : setInternalEdit && setInternalEdit({
                                                                ...internalEdit,
                                                                notes3: e.target.value
                                                            })}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            }
                                            {(editting || outerDoc) &&
                                                <Grid container xs={12} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    <Grid item xs={4}>
                                                        <Typography variant='body2'>
                                                            {notes4}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Input
                                                            value={internalEdit?.notes4 ?? document?.notes4}
                                                            onChange={(e) => outerDoc ? outerDoc(prev => {
                                                                return {...prev, notes4: e.target.value}
                                                            }) : setInternalEdit && setInternalEdit({
                                                                ...internalEdit,
                                                                notes4: e.target.value
                                                            })}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            }
                                            {(editting || outerDoc) &&
                                                <Grid container xs={12} sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}>
                                                    <Grid item xs={4}>
                                                        <Typography variant='body2'>
                                                            {notes5}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={8}>
                                                        <Input
                                                            value={internalEdit?.notes5 ?? document?.notes5}
                                                            onChange={(e) => outerDoc ? outerDoc(prev => {
                                                                return {...prev, notes5: e.target.value}
                                                            }) : setInternalEdit && setInternalEdit({
                                                                ...internalEdit,
                                                                notes5: e.target.value
                                                            })}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            }

                                        </>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>

                {supplierEditable ? (
                    <SupplierHandLines
                        rows={internalEdit?.handArray ?? document.handArray ?? []}
                        onChange={(handArray) => setInternalEdit && setInternalEdit({
                            ...internalEdit,
                            handArray,
                            lines_edit: 1
                        })}
                    />
                ) : document.handArray && ((document.handArray?.[0].value !== 0 && document.handArray?.[0].amount !== 0) || (document.handArray?.[0].tax !== 0)) ? (
                    <>
                        <TableContainer component={Paper}>
                            <Table aria-label="">
                                <TableHead>
                                    <TableRow>
                                        <TableCell style={{fontSize: "0.9rem", fontWeight: "bold", textAlign: "right"}}
                                                   align="right">תיאור ידני</TableCell>
                                        <TableCell style={{fontSize: "0.9rem", fontWeight: "bold", textAlign: "right"}}
                                                   align="right">כמות</TableCell>
                                        <TableCell style={{fontSize: "0.9rem", fontWeight: "bold", textAlign: "right"}}
                                                   align="right">{`מחיר${customer!.includeTax ? '(כולל)' : ''}`}</TableCell>
                                        <TableCell style={{fontSize: "0.9rem", fontWeight: "bold", textAlign: "right"}}
                                                   align="right">{`סהכ${customer!.includeTax ? '(כולל)' : ''}`}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {document.handArray.map((row) => (
                                        <TableRow
                                            key={row.description}
                                            sx={{'&:last-child td, &:last-child th': {border: 0}}}
                                        >
                                            <TableCell style={{textAlign: "right"}}
                                                       align="right">{row.description}</TableCell>
                                            <TableCell style={{textAlign: "right"}}
                                                       align="right">{row.amount}</TableCell>
                                            <TableCell style={{
                                                textAlign: "right",
                                                minWidth: row.value.toString().length * 20 < 121 ? row.value.toString().length * 20 : 115
                                            }} align="right">{row.value}<IconCurrencyShekel size={10}
                                                                                            color='black'/></TableCell>
                                            <TableCell style={{textAlign: "right"}} align="right">{
                                                roundDecimals((row.value * row.amount), 2)
                                            }
                                                <IconCurrencyShekel size={10} color='black'/>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ) : undefined}
                {pfromNotes.length > 0 ? (
                    <>
                        <Grid item xs={12}>
                            <Typography variant='subtitle1' fontWeight='bold'>
                                סיכום מוצרים מתעודות
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <CustomerProductsPreviewList products={pfromNotes.sort((a, b) => (
                                (a?.notePlacement != undefined || b?.notePlacement != undefined) ? ((a?.notePlacement ?? 0) - (b?.notePlacement ?? 0)) :
                                    (a?.position ?? 2000 - b?.position ?? 2000))
                            )}
                                                         withReturns={document.type !== 'refund' && document.type !== 'sup_refund'}
                                                         disableHoverableIncrementer={document.status === 'approved'}
                                                         date={document.date}
                                                         id={customer.id}
                                                         sup={isSupplierDocument(document.type)}

                            />
                        </Grid>
                    </>
                ) : undefined}

                {document.products.length > 0 || onProductChange ? (
                    <>
                        <Grid item xs={12}>
                            <Typography variant='subtitle1' fontWeight='bold'>
                                מוצרים
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <CustomerProductsPreviewList products={document.products.sort((a, b) => (
                                (a?.notePlacement != undefined || b?.notePlacement != undefined) ? ((a?.notePlacement ?? 0) - (b?.notePlacement ?? 0)) :
                                    (a?.position ?? 2000 - b?.position ?? 2000))
                            )}
                                                         withReturns={document.type !== 'refund' && document.type !== 'sup_refund'}
                                                         disableHoverableIncrementer={!supplierEditable && document.status === 'approved'}
                                                         onProductChange={onProductChange} date={shownDate}
                                                         id={customer.id}
                                                         orderProducts={displayOrderProducts && !editting ? deserializeDocument(document?.nkObject?.getConnectedOrder())?.products ?? [] : undefined}
                                                         displayOrderProducts={!editting && displayOrderProducts}
                                                         displayCost={showCostAndGain}
                                                         sup={isSupplierDocument(document.type)}
                            />
                        </Grid>
                    </>
                ) : undefined}
                {document.round !== 0 ?
                    <>
                        <Grid item xs={6}>
                            <Typography variant='subtitle1' fontWeight='bold'>
                                עיגול סכום
                            </Typography>
                        </Grid>
                        <Grid item xs={5}>
                            <Typography style={{direction: 'ltr'}} variant='subtitle1' fontWeight='bold'>
                                {roundDecimals(document.round, 4)} <IconCurrencyShekel size={10} color='black'/>
                            </Typography>

                        </Grid>

                    </> : undefined}
                <Grid item xs={12}>
                    <SubCard
                        sx={{
                            bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light
                        }}
                    >
                        <Grid container rowSpacing={0}>
                            <Grid item xs={6}>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    סהכ לפני מעמ
                                </Typography>
                            </Grid>
                            <Grid item style={{direction: 'ltr'}} xs={5}>
                                {supplierEditable ? (
                                    <SupplierMoneyInput
                                        value={Number(shown.preValue ?? 0)}
                                        onChange={(raw) => applySupplierMoneyEdit(internalEdit, document, 'pre', raw, setInternalEdit)}
                                    />
                                ) : (
                                    <>
                                        <IconCurrencyShekel size={10} color='black'/>
                                        {roundDecimals(document.preValue ?? 0).toLocaleString()}
                                    </>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <SupplierTotalsDiscountLabel
                                    editing={supplierEditable}
                                    discount={Number(shown.discount ?? 0)}
                                    onChange={(raw) => applySupplierMoneyEdit(internalEdit, document, 'discount', raw, setInternalEdit)}
                                />
                            </Grid>
                            <Grid item style={{direction: 'ltr'}} xs={5}>
                                <IconCurrencyShekel size={10} color='black'/>
                                {roundDecimals((shown.mainValue ?? 0) - (shown.taxValue ?? 0) - (shown.preValue ?? 0)).toLocaleString()}

                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    סהכ אחרי
                                </Typography>
                            </Grid>
                            <Grid item style={{direction: 'ltr'}} xs={5}>
                                <IconCurrencyShekel size={10} color='black'/>
                                {roundDecimals((shown.mainValue ?? 0) - (shown.taxValue ?? 0)).toLocaleString()}

                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                                    מע"מ
                                </Typography>
                            </Grid>
                            <Grid item style={{direction: 'ltr'}} xs={5}>
                                {supplierEditable ? (
                                    <SupplierMoneyInput
                                        value={Number(shown.taxValue ?? 0)}
                                        onChange={(raw) => applySupplierMoneyEdit(internalEdit, document, 'tax', raw, setInternalEdit)}
                                    />
                                ) : (
                                    <>
                                        <IconCurrencyShekel size={10} color='black'/>
                                        {roundDecimals(((document.taxValue) ? document.taxValue : 0)).toLocaleString()}
                                    </>
                                )}
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant='subtitle1' fontWeight='bold'>
                                    <FormattedMessage
                                        id='Total'/>
                                </Typography>
                            </Grid>
                            <Grid item style={{direction: 'ltr'}} xs={5}>
                                {supplierEditable ? (
                                    <SupplierMoneyInput
                                        value={Number(shown.mainValue ?? 0)}
                                        onChange={(raw) => applySupplierMoneyEdit(internalEdit, document, 'total', raw, setInternalEdit)}
                                    />
                                ) : (
                                    <>
                                        <IconCurrencyShekel size={10} color='black'/>
                                        {roundDecimals(document.mainValue).toLocaleString()}
                                    </>
                                )}
                            </Grid>
                            {(document?.value_left || (supplierEditable && document.type !== 'sup_tax_invoice')) ? <>
                                <Grid item xs={6}>
                                    <Typography variant='subtitle1' fontWeight='bold' color={'red'}>
                                        סהכ פתוח לחשבונית
                                    </Typography>
                                </Grid>
                                <Grid item style={{direction: 'ltr', color: 'red'}} xs={5}>
                                    {supplierEditable ? (
                                        <SupplierMoneyInput
                                            value={Number(shown.value_left ?? 0)}
                                            onChange={(raw) => applySupplierMoneyEdit(internalEdit, document, 'left', raw, setInternalEdit)}
                                        />
                                    ) : (
                                        <>
                                            <IconCurrencyShekel size={10} color='black'/>
                                            ({roundDecimals(document.value_left).toLocaleString()})
                                        </>
                                    )}
                                </Grid>
                            </> : undefined}
                            {showCostAndGain && gain > 0 ? <>
                                <Grid item xs={6}>
                                    <Typography variant='subtitle1' fontWeight='bold'>
                                        (רווח לפני מעמ)
                                    </Typography>
                                </Grid>
                                <Grid item style={{direction: 'ltr'}} xs={5}>
                                    <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.5}}>
                                        <Box sx={{color: profit >= 0 ? '#1b5e20' : '#b71c1c'}}>
                                            (<IconCurrencyShekel size={10} color={profit >= 0 ? '#1b5e20' : '#b71c1c'}/>
                                            {profit.toLocaleString()})
                                        </Box>
                                        <Typography variant='body2' component='span' sx={{color: 'black'}}>
                                            [{profitPercent}%]
                                        </Typography>
                                    </Box>
                                </Grid>
                            </> : undefined}

                            {byProducts ? (
                                <Grid item xs={12} sx={{display: 'flex', justifyContent: 'flex-start', mt: 0.5}}>
                                    <Tooltip title={hideCostAndGain ? 'הצג עלות ורווח' : 'הסתר עלות ורווח'}>
                                        <IconButton
                                            size="small"
                                            onClick={() => setHideCostAndGain((v) => !v)}
                                            aria-label={hideCostAndGain ? 'הצג עלות ורווח' : 'הסתר עלות ורווח'}
                                        >
                                            {hideCostAndGain
                                                ? <IconEyeOff size={18} />
                                                : <IconEye size={18} />}
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            ) : undefined}

                        </Grid>
                        {document?.type === "invoice" && (document?.orderId && document?.nkObject?.getConnectedOrder()?.getKartonString()) ?
                            <Grid item xs={12}>
                                <Typography variant='subtitle1'>
                                    ליקוט: {document?.nkObject?.getConnectedOrder()?.getKartonString()}
                                </Typography>
                            </Grid> : undefined
                        }

                        <Grid item xs={12}>
                            <Grid item xs={12}>
                                <PreviewDocumentComments outerDoc={outerDoc} comment={document.comment} doc={document}
                                                         pool={pool}/>
                            </Grid>
                        </Grid>


                    </SubCard>
                </Grid>
            </Grid>
            {receiptOpen && !onCreateReceipt ? (
                <Dialog
                    maxWidth="md"
                    fullWidth
                    open
                    onClose={closeReceiptDialog}
                >
                    <DocumentNew
                        customerId={String(document.entId)}
                        type={receiptType}
                        refId={[document.id]}
                        types={[document.type]}
                        onIssued={refreshInvoiceAfterReceipt}
                    />
                </Dialog>
            ) : undefined}
        </SubCard>
    );
};

export {canIssueReceiptFromInvoice};
export default InvoicePreview;
